import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ThemeButton from 'shared/ui/Button';

import { readSocketData } from 'shared/lib/fetchSocket';
import { buildPlHbl } from '../../lib/packingListHblAdapter';
import { isPackingListTab, workspaceKey } from '../../lib/packingListWire';
import { uomMasterLoaded } from '../../store/packingListSlice';
import PackingListWorkspace, { PackingListUploadDialog, ParsedNoticeToast } from '../PackingList';

/** 선적별로 마지막에 보던 HBL 탭. 상세 패널이 스크롤로 재마운트돼도 같은 HBL에 머문다. */
const lastHblTabs = new Map();

/**
 * 상세 패널의 Order Management 탭. HBL별 탭을 두고 선택한 HBL의 Packing List 작업 영역을 인라인으로 보여준다.
 */
export default function OrderManagementPanel({ mainRow, tabConfig }) {
    const dispatch = useDispatch();
    const tabValue = useSelector((state) => state.tabValueForm);
    const uomCount = useSelector((state) => state.packingList.uomMaster.length);
    const hblData = useSelector((state) => state[tabConfig.houseData]);
    const containerData = useSelector((state) => state[tabConfig.containerData]);
    const tradePartners = useSelector((state) => state.tradePartnerForm);
    const [tabIndex, setTabIndexState] = useState(() => lastHblTabs.get(mainRow.id) ?? 0);
    const setTabIndex = (index) => {
        lastHblTabs.set(mainRow.id, index);
        setTabIndexState(index);
    };
    /** 업로드 다이얼로그. null이면 닫힘, { docId }면 그 초안부터 이어서 연다. { editDoc }이면 그 확정 문서를 수정 모드로 연다. */
    const [uploadDialog, setUploadDialog] = useState(null);

    /** 항공은 Alias 행을 배분 단위로 쓰고, 컨테이너 테이블이 없는 타입은 빈 목록으로 두고 HBL만 나열한다. */
    const hbls = useMemo(() => {
        const isAir = tabConfig.tableName[0].startsWith('il_air_');
        return (hblData ?? [])
            .filter((item) => item[tabConfig.keyName[0]] === mainRow.id)
            .slice()
            .sort((a, b) => a.id - b.id)
            .map((row) => buildPlHbl({ hblRow: row, containerRows: (containerData ?? []).filter((c) => c[tabConfig.keyName[1]] === row.id), mainRow, tradePartners, isAir }));
    }, [hblData, containerData, tradePartners, mainRow, tabConfig]);

    /** HBL이 삭제돼 선택 인덱스가 범위를 벗어나면 첫 탭으로 돌아간다. */
    useEffect(() => {
        if (tabIndex >= hbls.length) setTabIndex(0);
    }, [hbls.length, tabIndex]);

    /** 수량 단위 마스터(il_opus_volume_unit). 검증·단위 선택이 함께 쓰므로 한 번만 읽어 둔다. */
    useEffect(() => {
        if (uomCount > 0) return;
        readSocketData(dispatch, tabValue, 'cell', 'il_opus_volume_unit', '', '', (rows) =>
            dispatch(uomMasterLoaded([...new Set((rows ?? []).map((r) => String(r?.name ?? r ?? '').trim().toUpperCase()).filter(Boolean))]))
        );
    }, [dispatch, tabValue, uomCount]);

    if (!isPackingListTab(tabValue)) {
        return <Typography sx={{ fontSize: 12, color: 'text.secondary', p: 2 }}>Order Management is available for Ocean Import and Air Import shipments only.</Typography>;
    }
    if (hbls.length === 0) {
        return <Typography sx={{ fontSize: 12, color: 'text.secondary', p: 2 }}>No HBL on this shipment yet. Add an HBL in the Operation view to manage packing lists.</Typography>;
    }
    const selected = hbls[Math.min(tabIndex, hbls.length - 1)];
    const scope = { tab: tabValue, shipmentId: mainRow.id, houseId: selected.id };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={Math.min(tabIndex, hbls.length - 1)} onChange={(_, v) => setTabIndex(v)} variant="scrollable" scrollButtons="auto" indicatorColor="secondary" sx={{ minHeight: '35px', height: '36px', '& .MuiTabs-flexContainer': { gap: 0.5 }, '& .MuiButtonBase-root': { border: 'none', p: 0 } }}>
                    {hbls.map((h) => (
                        <Tab
                            key={h.id}
                            sx={{
                                width: '130px',
                                minHeight: '35px',
                                borderRadius: '5px',
                                border: '1px solid #dfdfdf',
                                bgcolor: 'white',
                                fontSize: '11px',
                                fontWeight: 500,
                                textTransform: 'none',
                                '&.Mui-selected': { bgcolor: '#dfdfdf' },
                            }}
                            label={h.hbl || ''}
                        />
                    ))}
                </Tabs>
                <ThemeButton text="Upload" icon={<UploadFileIcon />} onClick={() => setUploadDialog({ docId: null })} sx={{ height: '27px', fontSize: '11px', minWidth: '70px', flexShrink: 0, mb: 0.5 }} />
            </Box>
            <PackingListWorkspace key={selected.id} hbl={selected} scope={scope} onOpenDoc={(docId) => setUploadDialog({ docId })} onEditDoc={(doc) => setUploadDialog({ docId: doc.id, editDoc: doc })} />
            {uploadDialog && <PackingListUploadDialog open hbl={selected} scope={scope} docId={uploadDialog.docId} editDoc={uploadDialog.editDoc ?? null} onClose={() => setUploadDialog(null)} />}
            <ParsedNoticeToast enabled={!uploadDialog} currentKey={workspaceKey(tabValue, selected.id)} onOpen={(docId) => setUploadDialog({ docId })} />
        </Box>
    );
}
